// SF5 — Late Shipment Rate Tracker.
//
// This is Amazon's own number, read from the account health report rather than
// calculated here. The PRD's credibility test is that a rate we derived should
// approximate Amazon's; showing Amazon's makes them identical by construction,
// and it is the only version a seller can reconcile against Seller Central.
//
// Merchant-fulfilled only, and that is the whole point. Eight percent late on
// two hundred self-shipped orders, blended with two thousand Amazon-fulfilled
// ones, reads as under one percent and a seller believes they are safe. Amazon
// volume sits beside the rate as context so the scope is unambiguous.
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { FiAlertTriangle, FiCheckCircle, FiInfo } from 'react-icons/fi';

import AuthContext from '../../../../context/AuthContext';
import local from '../OrderFulfillment.module.css';
import { getLateShipment } from '../services/orderAnalyticsApi';

const inr = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });

const count = (value) =>
  value === null || value === undefined ? '—' : inr.format(value);

const percent = (ratio, digits = 2) =>
  ratio === null || ratio === undefined ? '—' : `${(ratio * 100).toFixed(digits)}%`;

const LateShipment = () => {
  const authCtx = useContext(AuthContext);
  const token = authCtx?.token;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      setData(await getLateShipment(token));
    } catch (err) {
      setError(err);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const metric = data?.metric;
  const context = data?.context;
  const good = metric?.within_target;

  return (
    <section className={local.card}>
      <div className={local.cardHead}>
        <h2 className={local.cardTitle}>Late shipment rate</h2>
        {metric?.window?.start && (
          <span className={local.amazonTag}>
            Amazon’s window · {metric.window.start} to {metric.window.end}
          </span>
        )}
      </div>
      <p className={local.muted}>
        Amazon’s own figure, on the orders you dispatch yourself. Amazon ships
        its own orders and does not hold you responsible for them, so they are
        left out — including them would dilute the rate and hide a problem on
        the half you control.
      </p>

      {error && (
        <div className={`${local.banner} ${error.reportUnavailable ? local.bannerWarn : local.bannerError}`}>
          <FiAlertTriangle className={local.bannerIcon} />
          <div>
            <strong>
              {error.notConnected ? 'No Amazon account connected'
                : error.reportUnavailable ? 'Account health report not synced' : 'Could not load the rate'}
            </strong>
            <div>{error.message}</div>
          </div>
        </div>
      )}

      {!error && loading && !data && <div className={local.emptyCell}>Loading…</div>}

      {/* Not applicable is a distinct state from nought percent. A seller who
          ships nothing themselves has never been measured on this. */}
      {!error && data && !data.applies && (
        <div className={local.allClear}>
          <FiCheckCircle />
          {data.not_applicable_reason}
        </div>
      )}

      {!error && data?.applies && !metric && (
        <div className={`${local.banner} ${local.bannerWarn}`}>
          <FiAlertTriangle className={local.bannerIcon} />
          <div>
            <strong>Amazon has not reported this yet</strong>
            <div>
              The account health report synced without a late shipment figure in
              it, so nothing is shown rather than a number derived here.
            </div>
          </div>
        </div>
      )}

      {!error && metric && (
        <>
          <div className={local.actorRow}>
            <div className={`${local.actorTile} ${good ? local.measured : local.breach}`}>
              <div className={local.todayLabel}>Your rate</div>
              <div className={local.todayValue}>{percent(metric.rate)}</div>
              <div className={local.todayFoot}>
                {count(metric.count)} late of {count(metric.orders)} orders you shipped
              </div>
            </div>
            <div className={local.actorTile}>
              <div className={local.todayLabel}>Amazon’s target</div>
              <div className={local.todayValue}>
                {metric.target_condition === 'LESS_THAN' ? 'under ' : ''}
                {percent(metric.target, 0)}
              </div>
              <div className={local.todayFoot}>
                <span className={good ? local.up : local.down}>
                  {good ? 'within target' : 'over target'}
                </span>
                {metric.status ? ` · Amazon says ${metric.status.toLowerCase()}` : ''}
              </div>
            </div>
            <div className={local.actorTile}>
              <div className={local.todayLabel}>What this covers</div>
              <div className={local.todayValue}>
                {count(context?.merchant_fulfilled_orders)}
              </div>
              <div className={local.todayFoot}>
                orders you dispatched · {count(context?.amazon_fulfilled_orders)}{' '}
                Amazon-fulfilled orders are outside this rate entirely
              </div>
            </div>
          </div>
        </>
      )}

      {data?.notes?.length > 0 && (
        <ul className={local.noteList}>
          {data.notes.map((note) => (
            <li key={note}><FiInfo className={local.noteIcon} />{note}</li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default LateShipment;
